import { useState, useRef } from "react";
import api from "@/lib/api";
import { guardarConfirmado, reportarErrorGuardado } from "@/lib/guardado";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, MapPinned, BedDouble } from "lucide-react";

const ZONA_TIPOS = [
  ["recamara", "Recámara"], ["bano", "Baño"], ["sala", "Sala"], ["cocina", "Cocina"],
  ["pasillo", "Pasillo"], ["exterior", "Exterior"], ["otro", "Otro"],
];

// ESPACIOS: usa ÚNICAMENTE las entidades existentes Zonas y Camas. No crea entidad Habitación.
export default function EspaciosTab({ sitio, zonas, camas, onReload, puedeGestionar = true, camasError = false, onReintentarCamas }) {
  const [nuevaZona, setNuevaZona] = useState({ nombre: "", tipo: "recamara" });
  const [nuevaCama, setNuevaCama] = useState({ etiqueta: "", zona_id: "" });
  // CONSOLA-02: una sola escritura en vuelo por control (impide el doble envío).
  const [guardando, setGuardando] = useState("");
  const enviandoRef = useRef(false);

  const addZona = async () => {
    if (enviandoRef.current) return;
    if (!nuevaZona.nombre.trim()) return toast.error("La zona necesita un nombre.");
    enviandoRef.current = true;
    setGuardando("zona");
    try {
      await guardarConfirmado({
        escribir: () => api.post("/zonas", { sitio_id: sitio.id, nombre: nuevaZona.nombre.trim(), tipo: nuevaZona.tipo }),
        refrescar: () => onReload(),
        exito: "Zona agregada.",
      });
      setNuevaZona({ nombre: "", tipo: "recamara" });
    } catch (e) { reportarErrorGuardado(e, { consultarEstado: onReload }); }
    finally { enviandoRef.current = false; setGuardando(""); }
  };
  const addCama = async () => {
    if (enviandoRef.current) return;
    if (!nuevaCama.etiqueta.trim()) return toast.error("La cama necesita una etiqueta.");
    enviandoRef.current = true;
    setGuardando("cama");
    try {
      await guardarConfirmado({
        escribir: () => api.post("/camas", { sitio_id: sitio.id, etiqueta: nuevaCama.etiqueta.trim(), zona_id: nuevaCama.zona_id || null }),
        refrescar: () => onReload(),
        exito: "Cama agregada.",
      });
      setNuevaCama({ etiqueta: "", zona_id: "" });
    } catch (e) { reportarErrorGuardado(e, { consultarEstado: onReload }); }
    finally { enviandoRef.current = false; setGuardando(""); }
  };

  const zonaNombre = (zid) => zonas.find((z) => z.id === zid)?.nombre || "Sin zona";

  return (
    <div className="space-y-4">
      {camasError && (
        <div role="alert" data-testid="site-camas-error"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-sm text-amber-800">
            No pudimos actualizar las camas de este sitio.
            <span className="block text-xs text-amber-700 mt-0.5">
              Se muestra la última lectura disponible (puede no estar al día). Las zonas y el resto del sitio sí están actualizados; no hay nada que volver a capturar.
            </span>
          </p>
          {onReintentarCamas && (
            <Button variant="outline" size="sm" className="rounded-lg shrink-0" data-testid="site-reintentar-camas"
              onClick={() => onReintentarCamas()}>Reintentar espacios</Button>
          )}
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
      <section className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <h3 className="text-base font-medium text-[#012472] flex items-center gap-2"><MapPinned strokeWidth={1.6} className="h-4 w-4" /> Zonas ({zonas.length})</h3>
        <div className="divide-y divide-gray-100">
          {zonas.length === 0 ? <p className="text-sm text-slate-400 py-2">Sin zonas.</p> :
            zonas.map((z) => (
              <div key={z.id} data-testid={`zona-row-${z.id}`} className="py-2.5 flex items-center justify-between">
                <span className="text-sm text-slate-700">{z.nombre}</span>
                <span className="text-xs rounded-full bg-slate-50 text-slate-500 px-2 py-0.5">{z.tipo}</span>
              </div>
            ))}
        </div>
        {puedeGestionar && (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-slate-500">Agrega una zona del sitio (recámara, baño, pasillo…) para ubicar dispositivos y camas.</p>
          <div className="flex gap-2">
            <Input data-testid="zona-nombre-input" aria-label="Nombre de la zona nueva" className="rounded-xl" placeholder="Nombre de zona" value={nuevaZona.nombre}
              onChange={(e) => setNuevaZona((s) => ({ ...s, nombre: e.target.value }))} />
            <Select value={nuevaZona.tipo} onValueChange={(v) => setNuevaZona((s) => ({ ...s, tipo: v }))}>
              <SelectTrigger className="rounded-xl w-[130px]" aria-label="Tipo de la zona nueva"><SelectValue /></SelectTrigger>
              <SelectContent>{ZONA_TIPOS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="add-zona-button" onClick={addZona} disabled={guardando === "zona"} aria-label="Agregar zona al sitio"
              className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white shrink-0"><Plus className="h-4 w-4 mr-1" /> Agregar zona</Button>
          </div>
        </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <h3 className="text-base font-medium text-[#012472] flex items-center gap-2"><BedDouble strokeWidth={1.6} className="h-4 w-4" /> Camas ({camas.length})
          {camasError && <span className="text-xs font-normal text-amber-700" data-testid="camas-ultima-lectura">· última lectura</span>}
        </h3>
        <div className="divide-y divide-gray-100">
          {camas.length === 0 ? <p className="text-sm text-slate-400 py-2">Sin camas.</p> :
            camas.map((c) => (
              <div key={c.id} data-testid={`cama-row-${c.id}`} className="py-2.5 flex items-center justify-between">
                <span className="text-sm text-slate-700">{c.etiqueta}</span>
                <span className="text-xs rounded-full bg-slate-50 text-slate-500 px-2 py-0.5">{zonaNombre(c.zona_id)}</span>
              </div>
            ))}
        </div>
        {puedeGestionar && (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-slate-500">Agrega una cama con su etiqueta y, si aplica, la zona donde está.</p>
          <div className="flex gap-2">
            <Input data-testid="cama-etiqueta-input" aria-label="Etiqueta de la cama nueva" className="rounded-xl" placeholder="Etiqueta de cama" value={nuevaCama.etiqueta}
              onChange={(e) => setNuevaCama((s) => ({ ...s, etiqueta: e.target.value }))} />
            <Select value={nuevaCama.zona_id || "none"} onValueChange={(v) => setNuevaCama((s) => ({ ...s, zona_id: v === "none" ? "" : v }))}>
              <SelectTrigger className="rounded-xl w-[130px]" aria-label="Zona de la cama nueva"><SelectValue placeholder="Zona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin zona</SelectItem>
                {zonas.map((z) => <SelectItem key={z.id} value={z.id}>{z.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="add-cama-button" onClick={addCama} disabled={guardando === "cama"} aria-label="Agregar cama al sitio"
              className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white shrink-0"><Plus className="h-4 w-4 mr-1" /> Agregar cama</Button>
          </div>
        </div>
        )}
      </section>
      </div>
    </div>
  );
}
